import * as focusTrap from 'focus-trap';
import { getFocusableItems } from './utils';

export default class UtilityDropdown {
  constructor(container) {
    this.container = container;
    this.dropdowns = [];
    this.activeDropdown = null;
    this.hasHandlers = {};
  }

  init() {
    this.setupDropdowns();
    document.addEventListener('click', this.handleDocumentClick.bind(this));
    document.addEventListener('keyup', this.handleEscapeKey.bind(this));
  }

  setupDropdowns() {
    const dropdownItems = this.container.querySelectorAll('.dropdown:has(.dropdown-menu.nav-utility-submenu)');

    dropdownItems.forEach(item => {
      const trigger = item.querySelector('a[aria-haspopup="true"]');
      const menu = item.querySelector('.dropdown-menu.nav-utility-submenu');

      // Double-check that both trigger and submenu exist
      if (trigger && menu) {
        const dropdown = {
          item,
          trigger,
          menu,
          isOpen: false,
          focusableElements: getFocusableItems(menu),
          trap: null
        };

        // Initialize menu state and attributes
        menu.setAttribute('inert', true);

        // Create focus trap
        dropdown.trap = focusTrap.createFocusTrap(menu, {
          escapeDeactivates: false,
          clickOutsideDeactivates: true,
          fallbackFocus: trigger,
          setReturnFocus: () => trigger,
          initialFocus: dropdown.focusableElements[0] || trigger
        });

        // Set up ARIA attributes
        const menuId = `utility-dropdown-${Math.random().toString(36).substr(2, 9)}`;
        menu.setAttribute('id', menuId);
        trigger.setAttribute('aria-controls', menuId);
        trigger.setAttribute('aria-expanded', 'false');

        // Add event listeners
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggleDropdown(dropdown);
        });

        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            this.toggleDropdown(dropdown);
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.openDropdown(dropdown);
          }
        });

        this.dropdowns.push(dropdown);
      }
    });
  }

  toggleDropdown(dropdown) {
    if (dropdown.isOpen) {
      this.closeDropdown(dropdown);
    } else {
      this.openDropdown(dropdown);
    }
  }

  openDropdown(dropdown) {
    // Close any other open dropdown
    if (this.activeDropdown && this.activeDropdown !== dropdown) {
      this.closeDropdown(this.activeDropdown);
    }

    dropdown.isOpen = true;
    dropdown.menu.classList.add('show');
    dropdown.trigger.classList.add('show');
    dropdown.trigger.setAttribute('aria-expanded', 'true');
    dropdown.menu.removeAttribute('inert');

    this.activeDropdown = dropdown;

    // Activate focus trap
    if (dropdown.focusableElements.length > 0) {
      dropdown.trap.activate({
        onPostActivate: () => {
          setTimeout(() => {
            if (dropdown.focusableElements[0]) {
              dropdown.focusableElements[0].focus();
            }
          }, 10);
        }
      });

      // Set up keyboard handlers for trap boundaries
      this.setupTrapHandlers(dropdown);
    }
  }

  closeDropdown(dropdown) {
    if (!dropdown.isOpen) return;

    dropdown.isOpen = false;
    dropdown.menu.classList.remove('show');
    dropdown.trigger.classList.remove('show');
    dropdown.trigger.setAttribute('aria-expanded', 'false');
    dropdown.menu.setAttribute('inert', true);

    // Deactivate focus trap
    if (dropdown.trap && dropdown.trap.active) {
      dropdown.trap.deactivate({
        returnFocus: true
      });
    }

    // Clean up handlers
    this.cleanupTrapHandlers(dropdown);

    if (this.activeDropdown === dropdown) {
      this.activeDropdown = null;
    }
  }

  handleEscapeKey(e) {
    if ((e.key === 'Escape' || e.keyCode === 27) && this.activeDropdown) {
      this.closeDropdown(this.activeDropdown);
      this.activeDropdown.trigger.focus();
    }
  }

  setupTrapHandlers(dropdown) {
    const firstItem = dropdown.focusableElements[0];
    const lastItem = dropdown.focusableElements[dropdown.focusableElements.length - 1];

    if (firstItem) {
      this.logKeydownHandler(firstItem, this.createFirstTabHandler(dropdown));
    }
    if (lastItem && lastItem !== firstItem) {
      this.logKeydownHandler(lastItem, this.createLastTabHandler(dropdown));
    }
  }

  cleanupTrapHandlers(dropdown) {
    if (dropdown.focusableElements) {
      dropdown.focusableElements.forEach(element => {
        this.cleanupKeydownHandler(element);
      });
    }

    if (dropdown.menu) {
      this.cleanupKeydownHandler(dropdown.menu);
    }
  }

  createFirstTabHandler(dropdown) {
    return (e) => {
      if (e.key === 'Tab' && e.shiftKey) {
        // Shift+Tab on first item - deactivate trap and focus trigger
        e.preventDefault();
        dropdown.trap.deactivate({
          onPostDeactivate: () => {
            dropdown.trigger.focus();
            this.closeDropdown(dropdown);
          }
        });
      }
    };
  }

  createLastTabHandler(dropdown) {
    return (e) => {
      if (e.key === 'Tab' && !e.shiftKey) {
        // Tab on last item - deactivate trap and continue normal flow
        e.preventDefault();
        dropdown.trap.deactivate({
          onPostDeactivate: () => {
            this.closeDropdown(dropdown);
          }
        });
      }
    };
  }

  logKeydownHandler(element, handler) {
    if (!element) return;

    this.cleanupKeydownHandler(element);
    this.hasHandlers[element] = handler;
    element.addEventListener('keydown', handler);
  }

  cleanupKeydownHandler(element) {
    if (!element) return;

    if (this.hasHandlers[element]) {
      element.removeEventListener('keydown', this.hasHandlers[element]);
      delete this.hasHandlers[element];
    }
  }

  handleDocumentClick(e) {
    if (!this.activeDropdown) return;

    // Check if click is outside the active dropdown and not on the trigger
    if (!this.activeDropdown.item.contains(e.target)) {
      this.closeDropdown(this.activeDropdown);
    }
  }
}